import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError, type AgentDetail, type Me, type ShareRole, type SharingInfo } from "../api.js";
import { errorText } from "../auth/Screens.js";
import { useI18n } from "../i18n.js";
import { Badge, Field, TextInput } from "./ui.js";

const GUEST_ROLES: ShareRole[] = ["viewer", "editor", "manager"];
const roleHelp = (r: ShareRole) => (r === "manager" ? "role.manager.agentHelp" : r === "editor" ? "role.editor.help" : "role.viewer.help");

/** Share dialog, People list and Transfer flow for one agent (docs/SHARING.md). */
export function AgentSharing({ api, me, id, onLeft }: { api: Api; me: Me; id: string; onLeft: () => void }) {
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<SharingInfo | null>(null);
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ShareRole>("viewer");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [toEmail, setToEmail] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [keep, setKeep] = useState<"none" | ShareRole>("none");
  const [offerSent, setOfferSent] = useState<{ email: string; expiresAt: string } | null>(null);

  const fail = (e: unknown) => setError(errorText(e, t("common.network")));
  const load = () => api.sharing(id).then(setInfo, fail);
  useEffect(() => {
    void load();
    api.getAgent(id).then(setAgent, () => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  if (!info) return <main className="page" id="main">{error ? <div className="banner warn" role="alert">{error}</div> : <p>{t("common.loading")}</p>}</main>;

  const name = (agent?.config.name as string | undefined) ?? "";
  const manages = info.yourRole === "manager" || info.yourRole === "owner";
  const fullControl = info.via === "account" && manages;
  // Owners and managers of the owning account may give the agent away (the server decides; this only hides the form).
  const mayTransfer = (info.yourRole === "owner" || info.yourRole === "manager") && info.via === "account";
  const grantable = fullControl ? GUEST_ROLES : GUEST_ROLES.filter((r) => r !== "manager");
  const mayEdit = (r: ShareRole) => manages && (fullControl || r !== "manager");

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      await api.shareAgent(id, email.trim(), role);
      setSent(email.trim());
      setEmail("");
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const doRole = async (userId: string, next: ShareRole) => {
    setError(null);
    try {
      await api.setShareRole(id, userId, next);
    } catch (err) {
      fail(err);
    }
    await load();
  };

  const remove = async (userId: string, who: string, you: boolean) => {
    if (!window.confirm(you ? t("share.leaveConfirm", { agent: name }) : t("share.removeConfirm", { name: who }))) return;
    setError(null);
    try {
      await api.removeShare(id, userId);
      if (you) onLeft();
      else await load();
    } catch (err) {
      fail(err);
    }
  };

  const offer = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.offerTransfer(id, { email: toEmail.trim(), confirmName: confirmName.trim(), keepAccess: keep });
      setOfferSent({ email: r.toEmail, expiresAt: r.expiresAt });
      setToEmail("");
      setConfirmName("");
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const date = (iso: string) => new Date(iso).toLocaleDateString(lang);

  return (
    <main className="page" id="main">
      <a href={`#/agents/${id}`}>{t("share.back")}</a>
      <div className="row between">
        <h1>
          {t("share.title")}: {name || "…"}
        </h1>
        {info.via === "share" && <Badge tone="info">{t("share.badge")}</Badge>}
      </div>
      {error && <div className="banner warn" role="alert">{error}</div>}
      <p className="hint">{t("share.leadsNote")}</p>
      <p className="hint">{info.via === "share" ? t("share.sharedBy", { account: info.ownerAccountName, role: t(`role.${info.yourRole as ShareRole}`) }) : t("share.ownedBy", { account: info.ownerAccountName })}</p>

      <h2>{t("share.people")}</h2>
      {info.accountTeamSize !== null && <p className="hint">{t("share.accountTeam", { n: info.accountTeamSize })}</p>}
      {info.people.length === 0 ? (
        <p className="hint">{t("share.noGuests")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("common.name")}</th>
                <th>{t("common.email")}</th>
                <th>{t("share.role")}</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {info.people.map((p) => (
                <tr key={p.userId}>
                  <td>
                    {p.name || p.email} {p.you && <span className="hint">{t("team.you")}</span>}
                  </td>
                  <td>{p.email}</td>
                  <td>
                    {mayEdit(p.role) ? (
                      <select aria-label={t("share.changeRole", { name: p.name || p.email })} value={p.role} onChange={(e) => void doRole(p.userId, e.target.value as ShareRole)}>
                        {grantable.map((r) => (
                          <option key={r} value={r}>
                            {t(`role.${r}`)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      t(`role.${p.role}`)
                    )}
                  </td>
                  <td>
                    {p.you ? (
                      <button className="btn ghost" onClick={() => void remove(p.userId, p.name || p.email, true)}>
                        {t("share.leave")}
                      </button>
                    ) : (
                      mayEdit(p.role) && (
                        <button className="btn ghost" aria-label={`${t("share.remove")} ${p.name || p.email}`} onClick={() => void remove(p.userId, p.name || p.email, false)}>
                          {t("share.remove")}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manages && (
        <>
          <h2>{t("share.invite")}</h2>
          {!me.user.emailVerified ? (
            <p className="hint">{t("team.verifyFirst")}</p>
          ) : (
            <form className="card" onSubmit={send} noValidate>
              <p className="hint">{t("share.inviteHelp")}</p>
              <TextInput label={t("share.email")} type="email" autoComplete="off" value={email} onChange={setEmail} required />
              <Field label={t("share.role")} htmlFor="share-role" help={t(roleHelp(role))}>
                <select id="share-role" value={role} aria-describedby="share-role-help" onChange={(e) => setRole(e.target.value as ShareRole)}>
                  {grantable.map((r) => (
                    <option key={r} value={r}>
                      {t(`role.${r}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <button className="btn primary" type="submit" disabled={busy || !email.trim()}>
                {busy ? t("share.sending") : t("share.send")}
              </button>
              {sent && <p role="status">{t("share.sent", { email: sent })}</p>}
            </form>
          )}

          <h2>{t("share.pending")}</h2>
          {info.invitations.length === 0 ? (
            <p className="hint">{t("share.pendingNone")}</p>
          ) : (
            <ul className="plain-list">
              {info.invitations.map((i) => (
                <li key={i.id} className="row between">
                  <span>
                    {i.email} · {t(`role.${i.role}`)} <span className="hint">({t("share.expires", { date: date(i.expiresAt) })})</span>
                  </span>
                  {mayEdit(i.role) && (
                    <button className="btn ghost" onClick={() => void api.revokeShareInvite(id, i.id).then(load, fail)}>
                      {t("share.revoke")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {info.via === "account" && (
        <>
          <h2>{t("share.transfer")}</h2>
          {!mayTransfer ? (
            <p className="hint">{t("share.transferOnlyOwner")}</p>
          ) : info.transfer ? (
            <div className="card">
              <p role="status">{t("share.transferOpen", { email: info.transfer.toEmail, date: date(info.transfer.expiresAt) })}</p>
              <button className="btn secondary" onClick={() => void api.cancelTransfer(id).then(load, fail)}>
                {t("share.transferCancel")}
              </button>
            </div>
          ) : (
            <form className="card" onSubmit={offer} noValidate>
              <p>{t("share.transferHelp")}</p>
              <p className="hint">{t("share.transferMoves")}</p>
              <p className="hint">{t("share.transferResets")}</p>
              <TextInput label={t("share.transferEmail")} type="email" autoComplete="off" value={toEmail} onChange={setToEmail} required />
              <Field label={t("share.transferKeep")} htmlFor="transfer-keep">
                <select id="transfer-keep" value={keep} onChange={(e) => setKeep(e.target.value as "none" | ShareRole)}>
                  <option value="none">{t("share.keep.none")}</option>
                  {GUEST_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`role.${r}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <TextInput label={t("share.transferConfirm", { name })} value={confirmName} onChange={setConfirmName} required />
              <button className="btn primary" type="submit" disabled={busy || !toEmail.trim() || confirmName.trim() !== name.trim() || !name.trim()}>
                {t("share.transferSend")}
              </button>
              {offerSent && <p role="status">{t("share.transferSent", { email: offerSent.email, date: date(offerSent.expiresAt) })}</p>}
            </form>
          )}
        </>
      )}
    </main>
  );
}
